import { useState, useEffect } from "react";
import { useUser } from "@/hooks/use-user";
import { useLocation } from "wouter";
import Layout from "@/components/layout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/api";  // Update import
import type { Vendor } from "db/schema";
import { Card, CardContent } from "@/components/ui/card";
import VendorForm from "@/components/vendor-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Mail, Phone, MapPin, Building } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Vendors() {
  const { user, isLoading: userLoading } = useUser();
  const [, setLocation] = useLocation();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); 

  useEffect(() => {
    if (!userLoading && !user) {
      setLocation("/login");
    }
  }, [user, userLoading, setLocation]);

  useEffect(() => {
    async function fetchVendors() {
      try {
        setLoading(true);
        // Updated query to fetch vendors without agency relation for now
        const { data, error } = await supabase
          .from('vendors')
          .select('*');

        if (error) throw error;
        console.log('Vendors data:', data); // Debug log
        setVendors(data || []);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    }

    fetchVendors();
  }, []);

  if (error) {
    return <div>Error loading vendors: {error.message}</div>;
  }

  if (loading || userLoading) {
    return <Layout><div>Loading...</div></Layout>;
  }

  if (!user) return null;

  const handleSuccess = async () => {
    setOpen(false);
    // Updated refresh query to match
    const { data, error } = await supabase
      .from('vendors')
      .select('*');
    
    if (!error && data) {
      setVendors(data);
    }
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight">Vendors</h2>
            <p className="text-muted-foreground">Manage your vendor relationships</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Vendor</DialogTitle>
              </DialogHeader>
              <VendorForm onSuccess={handleSuccess} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Search Bar */}
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredVendors.map((vendor) => (
            <Card
              key={vendor.id}
              className="relative cursor-pointer"
              onClick={() => {
                setLocation(`/vendor-details/${vendor.id}`);
              }}
            >
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{vendor.name}</h3>
                    <Badge
                      variant={vendor.status === "active" ? "default" : "secondary"}
                    >
                      {vendor.status}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Mail className="mr-2 h-4 w-4" />
                      {vendor.email}
                    </div>
                    {vendor.phone && (
                      <div className="flex items-center">
                        <Phone className="mr-2 h-4 w-4" />
                        {vendor.phone}
                      </div>
                    )}
                    {vendor.address && (
                      <div className="flex items-center">
                        <MapPin className="mr-2 h-4 w-4" />
                        {vendor.address}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
}
